/* eslint-disable */
import axios from "axios";

// Osen Partner API base URLs
const OSEN_PROD = process.env.REACT_APP_OSEN_PROD;
const OSEN_SANDBOX = process.env.REACT_APP_OSEN_SANDBOX;

/**
 * @param {object} store   redux store slice (needs store.OsenToken, optional store.OsenSandbox)
 */
const getBaseUrl = (store) => (store?.OsenSandbox ? OSEN_SANDBOX : OSEN_PROD);

const authHeaders = (store) => ({
  Authorization: `Bearer ${store.OsenToken}`,
  "Content-Type": "application/json",
});

/**
 * Validate a raw token before saving it
 * Returns { valid: true, store: {...}, token: {...} } or { valid: false, error: "..." }
 */
export async function validateOsenToken(apiToken, sandbox = false) {
  const base = sandbox ? OSEN_SANDBOX : OSEN_PROD;
  const { data } = await axios.post(
    `${base}/v1/users/auth/customers/validate-token`,
    { api_token: apiToken },
    { headers: { "Content-Type": "application/json" } }
  );
  return data;
}

/** Authenticated merchant account + current store (name, balance, etc.) */
export async function getOsenAccount(store) {
  const { data } = await axios.get(`${getBaseUrl(store)}/v1/users/auth/customers`, {
    headers: authHeaders(store),
  });
  return data;
}

/** Provinces + municipalities. Public, no auth. */
export async function getOsenProvinces(sandbox = false) {
  const base = sandbox ? OSEN_SANDBOX : OSEN_PROD;
  const { data } = await axios.get(`${base}/geo/provinces`);
  return data;
}

/**
 * Create a draft order then immediately validate it (submit to carrier).
 * Pass municipalityId resolved from getOsenProvinces().
 */
export async function createOsenOrder(store, orderPayload) {
  const base = getBaseUrl(store);
  const headers = authHeaders(store);

  const { data: created } = await axios.post(`${base}/v1/orders`, orderPayload, { headers });

  // submit to carrier right away (created order starts as DRAFT)
  const { data: validated } = await axios.patch(
    `${base}/v1/orders/${created.id}/validate`,
    {},
    { headers }
  );

  return { ...created, ...validated }; // has id, trackingId, referenceId
}

/** Cancel an order that hasn't been picked up yet (PLACED / PENDING_RECEPTION only). */
export async function cancelOsenOrder(store, orderId) {
  const { data } = await axios.patch(
    `${getBaseUrl(store)}/v1/orders/${orderId}/cancel`,
    {},
    { headers: authHeaders(store) }
  );
  return data;
}

/**
 * Paginated order listing. 
 * No bulk "trackings[]" endpoint exists on Osen so we pull everything updated in the window and match locally.
 */
export async function listOsenOrdersUpdatedSince(store, startIso, endIso) {
  const base = getBaseUrl(store);
  const headers = authHeaders(store);

  let skip = 0;
  const take = 100;
  let count = Infinity;
  const all = [];

  do {
    const { data } = await axios.get(`${base}/v1/orders`, {
      headers,
      params: {
        skip,
        take,
        "updatedAt[start]": startIso,
        "updatedAt[end]": endIso,
        "sort[updatedAt]": "ASC",
      },
    });

    all.push(...(data.data || []));
    count = data.metaData?.count ?? all.length;
    skip += take;
  } while (skip < count);

  return all;
}

/** Merged PDF of shipping labels for a batch of orders (accepts id / trackingId / referenceId). */
export async function downloadOsenLabels(store, orderIds = []) {
  const params = new URLSearchParams();
  orderIds.forEach((id) => params.append("orderIds[]", id));

  const response = await axios.get(
    `${getBaseUrl(store)}/orders/order-slips-pdf?${params.toString()}`,
    {
      headers: authHeaders(store),
      responseType: "blob",
    }
  );
  return response.data; // Blob — save as .pdf
}

/** Live tracking timeline for a single order, public, no auth, safe for customer-facing UI. */
export async function getOsenTrackingEvents(trackingId, category = "SIMPLIFIED") {
  const { data } = await axios.get(
    `${OSEN_PROD}/tracking/by-tracking-id/${trackingId}/events`,
    { params: { category } }
  );
  return data;
}